import React, { useEffect, useReducer, useState } from 'react';
import { SkillsSkeleton } from './skills.skeleton';
import { DropdownSearch, FilterByMenu, ListLayout, MenuItem, OrderButton, OrderType, PageHeader, Pager, Searchfield, XIcon, useMatchScreenWidth } from '@keplerco/core';
import { SkillListItem } from './skill-list-item/skill-list-item';
import classNames from 'classnames';
import styles from './skills.module.css';
import { useNavigate } from 'react-router-dom';
import { PageLoaderLayer } from '../../components/loading-handling/loader-layers/page-loader-layer/page-loader-layer';
import { PagePath } from '../../navigation/navigation.enums';
import { EmptyState } from '../../components/empty-state/empty-state';
import { themedAssetUrl } from '../../theme-asset-url';
import { AssessmentState } from '../../enums/assessmentState';
import { GetUserRoleListDto, SearchSkillResultDto } from '../../apis';
import { DefaultOptions, useApis, useFetchState, useSetFetchState } from '../../global-state';
import { PagerNavigationConnector } from '../../components/pager-connector/pager-connector';
import { FetchStatus, FetchType } from '../../enums/fetch';

const labelMap = new Map([
  [OrderType.Ascending, 'Low to High'],
  [OrderType.Descending, 'High to Low'],
]);

export interface SkillSearchRequest {
  search?: string;
  category?: string;
  pageSize: number;
  assessmentStatus?: string,
  sortAscending: boolean,
  roleId?: string;
  roleDesignationId?: string;
  page: number;
}

export function SkillsPage(): JSX.Element {
  const { skillApi, roleApi } = useApis();
  const [skills, setSkills] = useState<SearchSkillResultDto>();
  const isMobile = useMatchScreenWidth('mobile');

  const setFetchState = useSetFetchState();

  const [loading, setLoading] = useState<boolean>(true);

  const initialRequest = {
    search: undefined,
    category: undefined,
    roleId: undefined,
    assessmentStatus: undefined,
    sortAscending: true,
    roleDesignationId: undefined,
    pageSize: 6,
    page: 1
  };
  const [request, setRequest] = useState<SkillSearchRequest>(initialRequest);

  useEffect(() => {
    async function getData() {
      setLoading(true);
      setFetchState(FetchStatus.Active, FetchType.PageFetching);
      let category = request.category;
      if (category == 'Select a category') {
        category = '';
      }
      const response = await skillApi.skillSearchGet(
        request.search,
        category,
        request.roleId,
        request.roleDesignationId,
        request.assessmentStatus,
        request.sortAscending,
        request.pageSize,
        request.page, 
        DefaultOptions);

      setSkills(response);
      setLoading(false);
      setFetchState(FetchStatus.Inactive, FetchType.PageFetching);
    }
    getData();
  }, [request]);

  function onInputSearch(query: string) {
    setRequest({ ...request, search: query, page: 1 });
  }

  function onChangeSort(type: OrderType) {
    const sortAscending = type === OrderType.Ascending;
    setRequest({ ...request, sortAscending, page: 1 });
  }

  function onClickCompletionStatus(assessmentStatus: string) {
    setRequest({ ...request, assessmentStatus, page: 1 });
  }

  const menuItems: MenuItem<any>[] = [
    {
      text: 'All',
      onClick: () => onClickCompletionStatus(''),
    },
    {
      text: 'Not Assessed',
      onClick: () => onClickCompletionStatus(AssessmentState.NotAssessed),
    },
    {
      text: 'In Progress',
      onClick: () => onClickCompletionStatus(AssessmentState.InProgress),
    },
    {
      text: 'Assessed',
      onClick: () => onClickCompletionStatus(AssessmentState.Assessed),
    },
  ];
  function onClickClearFilters() {
    setRequest(initialRequest);
  }
  const [categories, setCategories] = useState<Array<string>>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('Select a category');


  const [roles, setRoles] = useState<Array<GetUserRoleListDto>>([]);
  const [selectedRole, setSelectedRole] = useState<string>('');


  useEffect(() => {
    async function loadCategories() {
      const response = await skillApi.skillCategoriesGet(DefaultOptions);
      setCategories(['Select a category', ...response]);
    }
    loadCategories();
  },
    []);

  useEffect(() => {
    async function loadRoles() {
      const response = await roleApi.roleNamesGet(DefaultOptions);
      setRoles([{ name: 'Select a role', id: '' }, ...response]);
    }
    loadRoles();
  },
    []);

  return (
    <React.Fragment>
      <PageLoaderLayer path={PagePath.skills} skeletonLoader={<SkillsSkeleton />}>
        <div className={classNames('wrapper stack', [styles.skillsWrapper])}>
          <PageHeader
            breadcrumbs={[
              {
                name: 'Skills',
                url: PagePath.skills,
              },
            ]}
            title={'Skills'}
            subtitle="These are the skills relevant to your roles, Each skill has it's highest roles required levels."
          />
          <div className={styles.skillsFilters}>
            <Searchfield value={request.search} responsive={isMobile} onInput={onInputSearch} />
            <OrderButton defaultOrderType={OrderType.Ascending} labelMap={labelMap} variant="normal" onChange={onChangeSort} />
            <DropdownSearch
              label="Category"
              items={categories
                .map(category => ({
                  value: category,
                  label: category,
                  onClick: () => {
                    setSelectedCategory(category);
                    setRequest({ ...request, category, page: 1 });
                  },
                  selected: selectedCategory === category,
                }))
              }
              dialogContained
            />
            <DropdownSearch
              label="Role"
              items={roles
                .map(role => ({
                  value: role.id!,
                  label: role.name!,
                  onClick: () => {
                    setSelectedRole(role.id!);
                    setRequest({ ...request, roleId: role.id!, page: 1 });
                  },
                  selected: selectedRole === role.id,
                }))
              }
              dialogContained
            />
            <FilterByMenu items={menuItems} defaultItem="All" />
            <button className={styles.skillsClearFiltersButton} style={{ visibility: !request.search && request.sortAscending === true && request.assessmentStatus === undefined ? 'hidden' : 'visible' }} type="button" onClick={onClickClearFilters}>
              Clear Filters
              <XIcon tone="secondary" />
            </button>
          </div>
          {!skills?.results?.length ? (
            <EmptyState title="No results" badgeUrl={themedAssetUrl('graphics/empty-state-clouds.graphic.svg')} />
          ) : (
            <ListLayout>
              {skills?.results?.map(skill => (
                <SkillListItem key={skill.skillId} {...skill} />
              ))}
            </ListLayout>
          )}
          {(!!skills && skills.totalPages! > 1) && (
            <PagerNavigationConnector
              defaultPageNumber={request.page}
              pageCount={skills?.totalPages ?? 0}
              onPageChange={page => setRequest(currentState => ({ ...currentState, page }))}
            >
              {connector => (<Pager {...connector} isLoading={loading} />)}
            </PagerNavigationConnector>
          )}
        </div>
      </PageLoaderLayer>
    </React.Fragment>
  );
}

